import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";

function useResponsiveFeedbackStyles() {
    const isMobile = window.innerWidth <= 600;
    const isTablet = window.innerWidth > 600 && window.innerWidth <= 900;
    return {
        container: {
            maxWidth: isMobile ? "98vw" : isTablet ? 600 : 700,
            margin: isMobile ? "12px auto" : "40px auto",
            padding: isMobile ? 10 : 32,
            borderRadius: 16,
            boxShadow: "var(--ae-shadow-soft)",
            background: "var(--ae-surface)",
            border: "1px solid var(--ae-border)",
            minHeight: isMobile ? "100vh" : undefined,
        },
        h1: {
            color: "var(--ae-primary-strong)",
            marginBottom: 8,
            fontSize: isMobile ? 22 : 28,
        },
        p: {
            color: "var(--ae-text-muted)",
            marginBottom: 24,
            fontSize: isMobile ? 14 : 18,
        },
        form: {
            marginBottom: 32,
        },
        textarea: {
            width: "100%",
            padding: isMobile ? 8 : 12,
            borderRadius: 8,
            border: "1px solid var(--ae-border)",
            fontSize: isMobile ? 14 : 16,
            marginBottom: 12,
            color: "var(--ae-text)",
        },
        button: {
            background: "linear-gradient(135deg, var(--ae-accent) 0%, #f8bc85 100%)",
            color: "var(--ae-primary-strong)",
            border: "1px solid #ffd7b0",
            borderRadius: 8,
            padding: isMobile ? "8px 18px" : "10px 28px",
            fontWeight: 700,
            fontSize: isMobile ? 14 : 16,
            cursor: "pointer",
            transition: "background 0.2s, color 0.2s",
        },
        thankyou: {
            color: "var(--ae-primary)",
            fontWeight: 500,
            marginBottom: 24,
            fontSize: isMobile ? 13 : 15,
        },
        feedbackList: {
            marginTop: 16,
        },
        feedbackTitle: {
            color: "var(--ae-primary-strong)",
            fontSize: isMobile ? 16 : 20,
            marginBottom: 12,
        },
        feedbackItem: {
            background: "var(--ae-surface-soft)",
            borderRadius: 8,
            padding: isMobile ? 10 : 16,
            marginBottom: 10,
            border: "1px solid var(--ae-border)",
        },
        feedbackTo: {
            color: "var(--ae-primary)",
            fontWeight: 600,
            fontSize: isMobile ? 13 : 15,
        },
        feedbackText: {
            color: "#000",
            marginTop: 4,
            fontSize: isMobile ? 13 : 15,
        },
        info: {
            color: "var(--ae-primary)",
            fontSize: isMobile ? 13 : 15,
            marginTop: 32,
        },
    };
}

function Feedback() {
    const { user, token } = useAuth() || { user: { role: "teacher" }, token: null };
    const [feedback, setFeedback] = useState<any>("");
    const [submitted, setSubmitted] = useState<any>(false);
    const [feedbackList, setFeedbackList] = useState<any>([]);
    const [loading, setLoading] = useState<any>(true);
    const [error, setError] = useState<any>("");
    const styles = useResponsiveFeedbackStyles();
    const role = user?.role || "teacher";

    useEffect(() => {
        async function fetchFeedback() {
            setLoading(true);
            setError("");
            try {
                const res = await fetch("/api/feedback", {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                if (!res.ok) throw new Error("Failed to fetch feedback");
                const data = await res.json();
                setFeedbackList(data.feedback || []);
            } catch (err: any) {
                setError("Could not load feedback. Please try again.");
            } finally {
                setLoading(false);
            }
        }
        if (token) fetchFeedback();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!feedback.trim()) return;
        setError("");
        try {
            const res = await fetch("/api/feedback", {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ feedback }),
            });
            if (!res.ok) throw new Error("Failed to submit feedback");
            setSubmitted(true);
            setFeedback("");
            const updated = await res.json();
            setFeedbackList(updated.feedback || []);
            setTimeout(() => setSubmitted(false), 3000);
        } catch (err: any) {
            setError("Could not submit feedback. Please try again.");
        }
    };

    const handleButtonMouseOver = e => {
        e.currentTarget.style.background = 'linear-gradient(135deg, var(--ae-primary) 0%, #2f66c9 100%)';
        e.currentTarget.style.color = '#ffe6cc';
    };
    const handleButtonMouseOut = e => {
        e.currentTarget.style.background = 'linear-gradient(135deg, var(--ae-accent) 0%, #f8bc85 100%)';
        e.currentTarget.style.color = 'var(--ae-primary-strong)';
    };

    return (
        <div style={styles.container}>
            <h1 style={styles.h1}>Feedback</h1>
            <p style={styles.p}>Clear, actionable feedback for learning growth and communication.</p>
            <form onSubmit={handleSubmit} style={styles.form}>
                <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Share feedback or next steps..."
                    rows={4}
                    style={styles.textarea}
                />
                <button
                    type="submit"
                    style={styles.button}
                    onMouseOver={handleButtonMouseOver}
                    onMouseOut={handleButtonMouseOut}
                    disabled={loading || !feedback.trim()}
                >
                    {loading ? "Loading..." : "Submit Feedback"}
                </button>
            </form>
            {submitted && <p style={styles.thankyou}>Thank you for your feedback!</p>}
            {error && <p style={{ color: "red", marginBottom: 12 }}>{error}</p>}
            <div style={styles.feedbackList}>
                <h2 style={styles.feedbackTitle}>Recent Feedback</h2>
                {loading ? (
                    <p>Loading feedback...</p>
                ) : (
                    <ul style={{ listStyle: "none", padding: 0 }}>
                        {feedbackList.length === 0 && <li>No feedback yet.</li>}
                        {feedbackList.map((f, i) => (
                            <li key={i} style={styles.feedbackItem}>
                                {role === "teacher" && f.student ? (
                                    <>
                                        <strong style={styles.feedbackTo}>To: {f.student}</strong>
                                        <div style={styles.feedbackText}>{f.feedback}</div>
                                    </>
                                ) : role === "student" && f.teacher ? (
                                    <>
                                        <strong style={styles.feedbackTo}>From: {f.teacher}</strong>
                                        <div style={styles.feedbackText}>{f.feedback}</div>
                                    </>
                                ) : (role === "parent" || role === "family") && f.teacher ? (
                                    <>
                                        <strong style={styles.feedbackTo}>From: {f.teacher}</strong>
                                        <div style={styles.feedbackText}>{f.feedback}</div>
                                    </>
                                ) : (role === "admin" || role === "school_admin") && f.staff ? (
                                    <>
                                        <strong style={styles.feedbackTo}>Staff: {f.staff}</strong>
                                        <div style={styles.feedbackText}>{f.feedback}</div>
                                    </>
                                ) : (
                                    <div style={styles.feedbackText}>{f.feedback}</div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div style={styles.info}>
                <strong>Why feedback?</strong><br />
                <span style={{ color: "var(--ae-accent)" }}>Actionable feedback helps students, families, and teachers work together for growth.</span>
            </div>
        </div>
    );
}

export default Feedback;